<script lang="ts">
  // A static thumbnail of the player's avatar WEARING a given outfit/costume, composited on a plain
  // 2D canvas (no Pixi/WebGL), so a whole grid of these is cheap (unlike one Pixi app per item). It
  // stacks the same waist-up cels PortraitAvatar uses, in the manifest's z-order, swapping in the
  // item's outfit slot. `drawImage` is display-only (never getImageData/toDataURL) so cross-origin
  // R2 art doesn't taint. Shared layers (body/hair/face) are one cached fetch across the grid.
  import { findOutfit, hoodForOutfit, type ChibiLook } from "@ygoevo/world-protocol";
  import type { PortraitManifest, PortraitSlot } from "@ygoevo/yo2-data";
  import { asset, loadWorldData } from "../../data.ts";

  let { look, sku, mirrored = false }: { look: ChibiLook; sku: string; mirrored?: boolean } = $props();

  const CANVAS = 768;
  let canvas: HTMLCanvasElement;

  /** Player's look with the item's outfit slot applied (dc/ac are mutually exclusive → clear the other). */
  function selection(): Record<string, { variant: string; color: number }> {
    const sel: Record<string, { variant: string; color: number }> = { ...(look.slots ?? {}) };
    const e = findOutfit(sku);
    if (e) {
      sel[e.def.slot] = { variant: e.def.variant, color: e.c.color };
      sel[e.def.slot === "dc" ? "ac" : "dc"] = { variant: "00", color: 1 };
      // Hooded cloaks: show the hood UP (and clear the other hair layers so it reads cleanly).
      const hood = hoodForOutfit(e.def.id);
      if (hood) {
        sel.h1 = { variant: hood, color: sel.h1?.color ?? 1 };
        sel.hs = { variant: "00", color: 1 };
        sel.h2 = { variant: "00", color: 1 };
      }
    }
    return sel;
  }

  async function paint() {
    const cx = canvas?.getContext("2d");
    if (!cx) return;
    let manifest: PortraitManifest;
    try {
      manifest = (await loadWorldData()).avatarsPortrait;
    } catch {
      return; // art is cosmetic, a failed load just leaves the frame empty
    }
    const slots = manifest.genders[look.gender]?.slots ?? [];
    const sel = selection();
    // Resolve each worn layer (cel + draw rect), in the manifest's back→front z order. A required slot
    // with no choice falls back to its first variant so the bust is always complete (mirrors PortraitAvatar).
    const layers: { path: string; x: number; y: number; w: number; h: number }[] = [];
    for (const slot of slots as PortraitSlot[]) {
      // Keep supporting layers coherent even when previewing an old saved look:
      // eye detail follows Eyes and is shaded with the Body skin colour.
      const s = slot.slot === "es"
        ? sel.ey && { variant: sel.ey.variant, color: sel.bo?.color ?? 1 }
        : sel[slot.slot];
      const variant = s?.variant ?? (slot.optional ? "00" : slot.variants[0]?.variant);
      if (!variant || variant === "00") continue;
      const v = slot.variants.find((x) => x.variant === variant);
      if (!v) continue;
      const byColor = v.cels[String(s?.color ?? 1)] ?? v.cels[String(v.colors[0])];
      if (!byColor) continue;
      const path = byColor.r ?? byColor.n;
      if (path) layers.push({ path: asset(path), x: v.rect[0], y: v.rect[1], w: v.rect[2], h: v.rect[3] });
    }
    const loaded = await Promise.all(
      layers.map(
        (l) =>
          new Promise<{ img: HTMLImageElement; l: (typeof layers)[number] } | null>((res) => {
            const img = new Image();
            img.onload = () => res({ img, l });
            img.onerror = () => res(null);
            img.src = l.path;
          }),
      ),
    );
    cx.clearRect(0, 0, CANVAS, CANVAS);
    cx.imageSmoothingQuality = "high";
    cx.save();
    if (mirrored) {
      cx.translate(CANVAS, 0);
      cx.scale(-1, 1);
    }
    for (const e of loaded) if (e) cx.drawImage(e.img, e.l.x, e.l.y, e.l.w, e.l.h);
    cx.restore();
  }

  $effect(() => {
    void [look.gender, JSON.stringify(look.slots), sku, mirrored];
    if (canvas) void paint();
  });
</script>

<canvas bind:this={canvas} width={CANVAS} height={CANVAS} class="size-full object-contain"></canvas>
